import type { Failure } from '@/api/errors';
import { headline, remedy } from '@/api/errors';
import { useTechnical } from '@/app/technical';
import { Kicker } from './primitives';

/**
 * What a refusal looks like to a person: what happened, and what to do
 * about it. The second line is the platform's own sentence wherever it
 * wrote one, never composed here — so what is explained cannot drift from
 * what is enforced.
 */
export function Refusal({ failure, compact }: { failure: Failure; compact?: boolean }) {
  const tech = useTechnical();
  const tone = refusalTone(failure);
  const second = remedy(failure, tech);
  const kicker =
    failure.kind === 'denied'
      ? `Refused · ${failure.denial.who} can move it`
      : failure.kind === 'misconfigured'
        ? 'Not switched on yet'
        : failure.kind === 'offline'
          ? 'Offline'
          : 'Refused';
  return (
    <div
      role="alert"
      className={`flex flex-col gap-2 rounded-brand border px-4 py-3 ${
        tone === 'danger' ? 'border-danger' : tone === 'warning' ? 'border-warning' : 'border-line'
      } ${compact ? '' : 'p-5'}`}
    >
      <Kicker tone={tone === 'danger' ? 'danger' : undefined}>{kicker}</Kicker>
      <p className={`m-0 font-semibold ${compact ? 'text-body' : 'text-panel'}`}>{headline(failure, tech)}</p>
      {second && <p className="m-0 max-w-[62ch] text-dense text-secondary">{second}</p>}
      {failure.kind === 'denied' && failure.resumesAt && (
        <p className="m-0 text-dense text-ink" data-testid="refusal-resumes">
          It opens again {liftsAt(failure.resumesAt)}.
        </p>
      )}
      {failure.kind === 'denied' && tech && (
        <span className="text-label text-meta">
          reason <code className="font-mono">{failure.reason}</code>
        </span>
      )}
    </div>
  );
}

/**
 * How loud a refusal is. A denial carries its own tone from the policy's
 * framing; a server fault is the platform's to own, and everything else —
 * offline, a missing setting — is a state, not a line crossed.
 */
export function refusalTone(failure: Failure): 'danger' | 'warning' | 'neutral' {
  return failure.kind === 'denied' ? failure.denial.tone : failure.kind === 'server' ? 'danger' : 'neutral';
}

/** The border a refusal's tone draws. Shared so a page and a panel agree. */
export const TONE_BORDER: Record<'danger' | 'warning' | 'neutral', string> = {
  danger: 'border-danger',
  warning: 'border-warning',
  neutral: 'border-control',
};

/**
 * When a refusal of pace lifts, in the reader's own clock. A gateway window
 * is a day counted from its first call, not any country's day, so there is
 * no other zone that decides it.
 */
export function liftsAt(iso: string): string {
  const at = new Date(iso);
  const day = at.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}
